import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ChevronRight, Check, Search, Layers, Loader2 } from 'lucide-react';
import { UserRole } from '@pms/shared';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { usePropertyContext, type Property } from '@/contexts/PropertyContext';
import { usePropertiesSearch, type PropertySearchRow } from '@/hooks/api/useProperties';

const PAGE_LIMIT = 20;

function toProperty(row: PropertySearchRow): Property {
  return {
    id: row.id,
    name: row.name,
    ...(row.code ? { code: row.code } : {}),
    ...(row.address ? { address: row.address } : {}),
  };
}

function secondaryLine(row: PropertySearchRow): string | undefined {
  if (row.code) return row.code;
  const loc = [row.city, row.country].filter(Boolean).join(', ');
  return loc || undefined;
}

/**
 * The one group property switcher (Hotel Groups F1). Replaces the old
 * clear→full-page-PropertySelector bounce with an accessible combobox popover:
 * server-side search (`q`), offset paging + "Load more", remembers the last
 * selection (via PropertyContext localStorage). The "All properties (portfolio)"
 * row is pinned ONLY for group admins (ADMIN + >1 property).
 *
 * Single-property groups (properties.length <= 1) render a static label with no
 * popover — today's behavior is preserved and single-property owners see no
 * change.
 */
export function PropertySwitcherPopover({ collapsed }: { collapsed: boolean }) {
  const { properties, selectedProperty, mode, selectProperty, selectPortfolio } =
    usePropertyContext();
  const { user } = useAuth();
  const navigate = useNavigate();

  const isGroupAdmin = user?.role === UserRole.ADMIN && properties.length > 1;
  const isMulti = properties.length > 1;

  const [open, setOpen] = useState(false);
  const [rawQ, setRawQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<PropertySearchRow[]>([]);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, isError, isFetching, refetch } = usePropertiesSearch(debouncedQ, {
    page,
    limit: PAGE_LIMIT,
  });

  // Debounce the search term (300ms).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(rawQ.trim()), 300);
    return () => clearTimeout(t);
  }, [rawQ]);

  // Reset paging + accumulator whenever the search term changes.
  useEffect(() => {
    setPage(1);
    setItems([]);
  }, [debouncedQ]);

  // Merge each fetched page into the accumulated list (dedupe by id).
  useEffect(() => {
    if (!data) return;
    const rows = data.data ?? [];
    setItems((prev) => {
      if (page === 1) return rows;
      const seen = new Set(prev.map((p) => p.id));
      return [...prev, ...rows.filter((r) => !seen.has(r.id))];
    });
  }, [data, page]);

  // Focus the search field when the popover opens.
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const total = data?.meta?.total ?? items.length;
  const hasMore = data ? (data.meta?.page ?? 1) < (data.meta?.totalPages ?? 1) : false;

  const listboxId = 'property-switcher-listbox';

  function handleSelect(row: PropertySearchRow) {
    selectProperty(toProperty(row));
    setOpen(false);
  }

  function handlePortfolio() {
    selectPortfolio();
    setOpen(false);
    navigate('/dashboard');
  }

  function moveFocus(delta: number) {
    const optionEls = containerRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
    if (!optionEls || optionEls.length === 0) return;
    const list = Array.from(optionEls);
    const active = document.activeElement as HTMLElement | null;
    const idx = active ? list.indexOf(active) : -1;
    const next = Math.max(0, Math.min(list.length - 1, idx + delta));
    list[next]?.focus();
  }

  function onListKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveFocus(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveFocus(-1);
    }
  }

  // ─── Trigger label ────────────────────────────────────────────────────────
  const triggerLabel = useMemo(() => {
    if (mode === 'portfolio') return 'All properties';
    if (selectedProperty) return selectedProperty.name;
    return 'Select Property';
  }, [mode, selectedProperty]);

  const triggerCode =
    mode === 'single' && selectedProperty?.code ? selectedProperty.code : undefined;

  // Single-property group → static, non-interactive label (today's behavior).
  if (!isMulti) {
    return (
      <div
        className={cn(
          'w-full flex items-center gap-2 border-b border-gray-100',
          collapsed ? 'justify-center px-2 py-3' : 'px-4 py-3',
        )}
      >
        <Building2 size={14} className="text-primary-600 shrink-0" aria-hidden="true" />
        {!collapsed && (
          <div className="flex-1 min-w-0 text-left">
            <p className="text-xs font-semibold text-primary-700 truncate">{triggerLabel}</p>
            {triggerCode && (
              <p className="text-[10px] text-gray-400 font-mono truncate">{triggerCode}</p>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative border-b border-gray-100">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'w-full flex items-center gap-2 transition-colors',
          'hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400',
          collapsed ? 'justify-center px-2 py-3' : 'px-4 py-3',
        )}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Switch property (current: ${triggerLabel})`}
      >
        {mode === 'portfolio' ? (
          <Layers size={14} className="text-primary-600 shrink-0" aria-hidden="true" />
        ) : (
          <Building2 size={14} className="text-primary-600 shrink-0" aria-hidden="true" />
        )}
        {!collapsed && (
          <>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-xs font-semibold text-primary-700 truncate">{triggerLabel}</p>
              {triggerCode && (
                <p className="text-[10px] text-gray-400 font-mono truncate">{triggerCode}</p>
              )}
            </div>
            <ChevronRight size={14} className="text-gray-400 shrink-0" aria-hidden="true" />
          </>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Property switcher"
          className="absolute left-2 right-2 top-full z-40 mt-1 rounded-xl border border-gray-200 bg-white shadow-lg"
          onKeyDown={onListKeyDown}
        >
          {/* Pinned portfolio row — group admins only */}
          {isGroupAdmin && (
            <button
              type="button"
              role="option"
              aria-selected={mode === 'portfolio'}
              onClick={handlePortfolio}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2.5 text-left border-b border-gray-100 transition-colors',
                'hover:bg-primary-50 focus-visible:outline-none focus-visible:bg-primary-50',
                mode === 'portfolio' ? 'bg-primary-50' : '',
              )}
            >
              <Layers size={16} className="text-primary-600 shrink-0" aria-hidden="true" />
              <span className="flex-1 text-sm font-medium text-gray-900">
                All properties (portfolio)
              </span>
              {mode === 'portfolio' && (
                <Check size={16} className="text-primary-600 shrink-0" aria-hidden="true" />
              )}
            </button>
          )}

          {/* Search */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input
                ref={searchRef}
                type="text"
                role="combobox"
                aria-expanded={open}
                aria-controls={listboxId}
                aria-autocomplete="list"
                value={rawQ}
                onChange={(e) => setRawQ(e.target.value)}
                placeholder="Search properties…"
                className="w-full rounded-lg border border-gray-200 pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
          </div>

          {/* Results */}
          <div id={listboxId} role="listbox" aria-label="Properties" className="max-h-64 overflow-y-auto">
            {isLoading && items.length === 0 ? (
              <div className="p-3 space-y-2" aria-hidden="true">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="h-8 rounded-lg bg-gray-100 animate-pulse" />
                ))}
              </div>
            ) : isError ? (
              <div className="p-4 text-center">
                <p className="text-sm text-gray-500 mb-2">Couldn't load properties.</p>
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  Retry
                </button>
              </div>
            ) : items.length === 0 ? (
              <p className="p-4 text-center text-sm text-gray-500">
                {debouncedQ ? `No properties match "${debouncedQ}"` : 'No properties'}
              </p>
            ) : (
              items.map((row) => {
                const isSelected = mode === 'single' && selectedProperty?.id === row.id;
                const secondary = secondaryLine(row);
                return (
                  <button
                    key={row.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(row)}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 text-left transition-colors',
                      'hover:bg-gray-50 focus-visible:outline-none focus-visible:bg-gray-50',
                      isSelected ? 'bg-primary-50' : '',
                    )}
                  >
                    <Building2 size={14} className="text-gray-400 shrink-0" aria-hidden="true" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 truncate">{row.name}</p>
                      {secondary && (
                        <p className="text-[11px] text-gray-400 truncate">{secondary}</p>
                      )}
                    </div>
                    {isSelected && (
                      <Check size={14} className="text-primary-600 shrink-0" aria-hidden="true" />
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Footer — count + load more */}
          {items.length > 0 && (
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-gray-100">
              <span className="text-[11px] text-gray-400" aria-live="polite">
                Showing {items.length} of {total}
              </span>
              {hasMore && (
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={isFetching}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
                >
                  {isFetching && <Loader2 size={12} className="animate-spin" aria-hidden="true" />}
                  Load more
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
